import React, { useState } from 'react';
import { Phone, MapPin, Clock, Menu, X, ChevronDown, Stethoscope, Heart, Users, Award, Leaf, Activity, Apple, Dumbbell, Pill } from 'lucide-react';

const HomePage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const services = [
    {
      icon: <Activity className="w-8 h-8" />,
      title: "Skin Disorders",
      description: "Eczema, Psoriasis, Acne, Vitiligo, Urticaria, Warts, and other dermatological conditions",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Respiratory Issues",
      description: "Asthma, Bronchitis, Allergic Rhinitis, Sinusitis, Chronic Cough, and breathing difficulties",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: <Leaf className="w-8 h-8" />,
      title: "Digestive Problems",
      description: "IBS, Constipation, Acidity, Gastritis, Piles, Liver disorders, and stomach ailments",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Women's Health",
      description: "PCOD, Menstrual disorders, Menopause issues, Fibroids, Leucorrhea, and hormonal imbalances",
      color: "from-rose-500 to-orange-500"
    },
    {
      icon: <Stethoscope className="w-8 h-8" />,
      title: "Children's Ailments",
      description: "Growth issues, Recurring infections, Tonsillitis, Adenoids, Bedwetting, and developmental concerns",
      color: "from-yellow-500 to-amber-500"
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Chronic Conditions",
      description: "Arthritis, Migraine, Thyroid disorders, Diabetes support, Anxiety, Depression, and autoimmune diseases",
      color: "from-indigo-500 to-violet-500"
    }
  ];

  const faqs = [
    {
      question: "What is Homeopathy?",
      answer: "Homeopathy is a natural system of medicine that works on the principle of 'like cures like'. It uses highly diluted substances to trigger the body's natural healing process. Homeopathy treats the person as a whole, considering physical, mental, and emotional aspects."
    },
    {
      question: "Is Homeopathy safe?",
      answer: "Yes, homeopathic medicines are extremely safe as they are made from natural substances in very minute doses. They have no side effects and are non-toxic. Homeopathy is safe for all age groups including infants, pregnant women, and elderly patients."
    },
    {
      question: "How long does Homeopathic treatment take?",
      answer: "The duration of treatment varies depending on the nature and chronicity of the disease. Acute conditions may show improvement within days, while chronic conditions might take a few weeks to months. The goal is permanent cure, not just symptomatic relief."
    },
    {
      question: "Can Homeopathy be taken along with other medicines?",
      answer: "Yes, homeopathic medicines can be safely taken alongside conventional medicines. However, it's important to inform your homeopathic doctor about all medications you're currently taking to ensure the best treatment approach."
    },
    {
      question: "Does Homeopathy work for all diseases?",
      answer: "Homeopathy is effective for a wide range of acute and chronic conditions. It works exceptionally well for functional disorders, psychosomatic ailments, and chronic diseases. However, for surgical emergencies and advanced pathological conditions, conventional treatment may be necessary."
    }
  ];

  const stats = [
    { number: "25+", label: "Years Experience" },
    { number: "10,000+", label: "Happy Patients" },
    { number: "100+", label: "Conditions Treated" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header/Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                <Leaf className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Dr SatDev's Homeo Clinic</h1>
                <p className="text-xs text-gray-600">Holistic Healing, Natural Care</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-8">
              {['home', 'about', 'doctor', 'approach', 'services', 'faq', 'contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item)}
                  className="text-gray-700 hover:text-green-600 font-medium capitalize transition-colors"
                >
                  {item}
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4">
              {['home', 'about', 'doctor', 'approach', 'services', 'faq', 'contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item)}
                  className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-green-50 capitalize"
                >
                  {item}
                </button>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-block">
                <span className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold">
                  Trusted Since 1999
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Natural Healing for a
                <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent"> Healthier You</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Experience the gentle power of homeopathy with 25+ years of expertise. 
                Dr. Vikram Chauhan offers personalized, holistic treatment for chronic and acute conditions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://wa.me/919855281615?text=Hello%2C%20I%20would%20like%20to%20book%20an%20appointment"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-4 rounded-lg font-semibold hover:shadow-xl transform hover:scale-105 transition-all text-center"
                >
                  Book Appointment via WhatsApp
                </a>
                <a
                  href="tel:+919855281615"
                  className="border-2 border-green-600 text-green-600 px-8 py-4 rounded-lg font-semibold hover:bg-green-50 transition-all text-center"
                >
                  Call Now
                </a>
              </div>
            </div>

            <div className="relative">
              {/* <div className="relative w-full h-96 lg:h-[500px] bg-gradient-to-br from-green-200 to-emerald-300 rounded-3xl overflow-hidden shadow-2xl">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <Stethoscope className="w-32 h-32 text-white mx-auto mb-4 opacity-50" />
                    <p className="text-white text-lg font-medium">Professional Homeopathic Care</p>
                  </div>
                </div>
              </div> */}
              <div className="relative w-full h-[450px] lg:h-[600px] rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="/images/clinic-hero.jpeg" 
                  alt="Dr SatDev's Homeo Clinic" 
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full opacity-20"></div>
              <div className="absolute -top-6 -left-6 w-24 h-24 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full opacity-20"></div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-20 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg text-center transform hover:scale-105 transition-all">
                <div className="text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                  {stat.number}
                </div>
                <div className="text-gray-600 mt-2">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              About Our Clinic
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-green-600 to-emerald-600 mx-auto"></div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="w-full h-96 bg-gradient-to-br from-blue-200 to-purple-300 rounded-3xl overflow-hidden shadow-xl">
                <div className="absolute inset-0 flex items-center justify-center">
                  <Heart className="w-32 h-32 text-white opacity-50" />
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h3 className="text-3xl font-bold text-gray-900">
                Your Trusted Partner in Holistic Health
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                Dr SatDev's Homeo Clinic has been serving the community of Dasuya for over 25 years, 
                providing compassionate and effective homeopathic treatment. Our clinic is built on 
                the foundation of trust, expertise, and genuine care for every patient.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                We believe in treating the person as a whole - addressing not just the symptoms, 
                but the root cause of illness. Our approach combines classical homeopathy with 
                modern diagnostic understanding to deliver the best possible outcomes.
              </p>

              <div className="grid grid-cols-2 gap-6 pt-6">
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Award className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Experienced</div>
                      <div className="text-sm text-gray-600">25+ Years</div>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Patient-Focused</div>
                      <div className="text-sm text-gray-600">Holistic Care</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Doctor Profile Section */}
      <section id="doctor" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Meet Your Doctor
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-green-600 to-emerald-600 mx-auto"></div>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
              <div className="grid md:grid-cols-5 gap-8">
                <div className="md:col-span-2 bg-gradient-to-br from-green-500 to-emerald-600 p-8 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-48 h-48 bg-white rounded-full mx-auto mb-4 flex items-center justify-center">
                      <Stethoscope className="w-24 h-24 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">Dr. Vikram Chauhan</h3>
                    <p className="text-green-100 text-lg">D.H.M.S (Punjab)</p>
                  </div>
                </div>

                <div className="md:col-span-3 p-8 space-y-6">
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">Qualifications</h4>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-gray-800 font-medium">D.H.M.S</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">Experience</h4>
                    <p className="text-gray-700 leading-relaxed">
                      With over 25 years of dedicated practice in homeopathy, Dr. Vikram Chauhan has successfully 
                      treated thousands of patients with various chronic and acute conditions. His expertise spans 
                      across multiple specialties including skin disorders, respiratory issues, digestive problems, 
                      and pediatric care.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">Approach</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Dr. Chauhan believes in individualized treatment, taking time to understand each patient's 
                      unique constitution and health history. His compassionate approach and commitment to patient 
                      well-being have earned him the trust of the community.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Holistic Approach Section */}
      <section id="approach" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Our Holistic Treatment Approach
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
              We believe in complete wellness through an integrated approach combining medication, nutrition, and lifestyle
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-green-600 to-emerald-600 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Homeopathic Medication */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transform hover:-translate-y-2 transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mb-6">
                <Pill className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Homeopathic Medication</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Personalized homeopathic remedies tailored to your unique constitution and health condition.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Natural & safe medicines</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>No side effects</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Treats root cause</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Boosts immunity</span>
                </li>
              </ul>
            </div>

            {/* Personalized Diet Plans */}
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transform hover:-translate-y-2 transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl flex items-center justify-center mb-6">
                <Apple className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Nutritional Guidance</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Customized diet plans designed to complement your treatment and enhance healing.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-orange-600 mr-2">✓</span>
                  <span>Condition-specific diets</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-600 mr-2">✓</span>
                  <span>Anti-inflammatory foods</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-600 mr-2">✓</span>
                  <span>Detox recommendations</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-600 mr-2">✓</span>
                  <span>Lifestyle eating habits</span>
                </li>
              </ul>
            </div>

            {/* Yoga & Exercise */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transform hover:-translate-y-2 transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-6">
                <Dumbbell className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Yoga & Exercise</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Simple yoga postures and exercises to support your healing and maintain wellness.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">✓</span>
                  <span>Therapeutic yoga asanas</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">✓</span>
                  <span>Breathing exercises (Pranayama)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">✓</span>
                  <span>Stress management techniques</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">✓</span>
                  <span>Gentle physical activity</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Why Holistic Approach */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-10 rounded-2xl">
            <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              Why This Integrated Approach Works
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  <strong>Faster Recovery:</strong> Combining medication with proper nutrition and exercise accelerates the healing process and helps your body respond better to treatment.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Long-lasting Results:</strong> Lifestyle modifications ensure that improvements are sustained even after treatment completion, reducing chances of recurrence.
                </p>
              </div>
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  <strong>Enhanced Well-being:</strong> A balanced diet and regular exercise improve energy levels, mental clarity, and overall quality of life alongside symptom relief.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Prevention Focus:</strong> Our approach not only treats existing conditions but also strengthens your body's natural defenses against future health issues.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
              We offer comprehensive homeopathic treatment for a wide range of conditions
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-green-600 to-emerald-600 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="group bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300"
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <div className="text-white">{service.icon}</div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-8 rounded-2xl">
              <p className="text-lg text-gray-700 mb-4">
                <strong>And Many More Conditions...</strong>
              </p>
              <p className="text-gray-600 max-w-3xl mx-auto">
                Homeopathy treats a wide spectrum of diseases including allergies, hormonal imbalances, 
                autoimmune disorders, joint pains, stress-related conditions, and much more. 
                Schedule a consultation to discuss your specific health concerns.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about homeopathy
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-green-600 to-emerald-600 mx-auto mt-6"></div>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex justify-between items-center hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-semibold text-gray-900 text-left">
                    {faq.question}
                  </span>
                  <ChevronDown
                    className={`w-6 h-6 text-green-600 transition-transform ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Visit Us
            </h2>
            <p className="text-xl text-gray-600">
              We're here to help you on your journey to better health
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-green-600 to-emerald-600 mx-auto mt-6"></div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-2xl">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Get In Touch</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Address</h4>
                      <p className="text-gray-700">
                        Dr SatDev's Homeo Clinic<br />
                        Library Chowk, Dasuya<br />
                        Opposite Sportking
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Phone</h4>
                      <a href="tel:+919855281615" className="text-green-600 hover:underline">
                        +91 98552 81615
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Working Hours</h4>
                      <p className="text-gray-700">
                        Monday - Saturday: 10:00 AM - 2:00 PM, 4:00 PM - 7:00 PM<br />
                        Sunday: 10:00 AM – 1:00 PM (Closed on last Sunday of the month)
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-green-200">
                  <a
                    href="https://wa.me/919855281615?text=Hello%2C%20I%20would%20like%20to%20book%20an%20appointment"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-4 rounded-lg font-semibold hover:shadow-xl transform hover:scale-105 transition-all text-center"
                  >
                    Book Appointment via WhatsApp
                  </a>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="h-96 lg:h-full bg-gray-200 rounded-2xl overflow-hidden shadow-xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3397.123456789012!2d75.65123456789012!3d31.82123456789012!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzHCsDQ5JzE2LjQiTiA3NcKwMzknMDQuNCJF!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Dr SatDev's Homeo Clinic Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                  <Leaf className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold">Dr SatDev's Homeo Clinic</h3>
              </div>
              <p className="text-gray-400">
                Providing trusted homeopathic care for over 25 years. Your health is our priority.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <div className="space-y-2">
                {['home', 'about', 'services', 'contact'].map((item) => (
                  <button
                    key={item}
                    onClick={() => scrollToSection(item)}
                    className="block text-gray-400 hover:text-white capitalize transition-colors"
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Contact Info</h4>
              <div className="space-y-2 text-gray-400">
                <p>Library Chowk, Dasuya</p>
                <p>Opposite Sportking</p>
                <a href="tel:+919855281615" className="block hover:text-white">
                  +91 98552 81615
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Dr SatDev's Homeo Clinic. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;